import React, { useEffect, useState } from 'react';

import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Button, Checkbox, Col, Form, Input, Row, Spin } from 'antd';
import { AES } from 'crypto-js';

import { User, EntityBuilding, SelfDirectedIra } from '../../assets/icons';
import AuthForm from '../../components/AuthForm/AuthForm';
import AuthInfo from '../../components/AuthInfo/AuthInfo';
import FloatingLabel from '../../components/FloatingLabel/FloatingLabel';
import EmailField from '../../components/SharedComponents/FormComponents/EmailField';
import PasswordField from '../../components/SharedComponents/FormComponents/PasswordField';
import SelectDropdown from '../../components/SelectDropdown/SelectDropdown';

import { EmailValidate, signUp } from '../../services/authApi/authApi';
import { submitSocialProfile } from '../../services/authApi/userApi';
import { clearSocialSignupState, getSocialSignupState } from '../../utils/socialSignup';
import { formatPlainPhoneNumber } from '../../utils/formatPhoneNumber';
import { preventReload } from '../../utils/preventReload';
import { toastNotify } from '../../utils/toastNotify';
import {
  FIRST_NAME_LENGTHS,
  FIRST_NAME_VALIDATION,
  LAST_NAME_LENGTHS,
  LAST_NAME_VALIDATION,
  ENTITY_NAME_LENGTHS,
  ENTITY_NAME_VALIDATION,
  ENTITY_TYPE_VALIDATION,
  STATE_OF_INCORPORATION_LENGTHS,
  STATE_OF_INCORPORATION_VALIDATION,
} from '../../utils/validationMessages';
import { alphabetSpaceRegex, isMobileDeviceDetectRegex, onlyAlphabetRegex } from '../../utils/Validations';
import { ACCOUNT_TYPES, ENTITY_TYPE_OPTIONS } from '../../constants/accountType.constants';

import './Signup.scss';
import { getCookieValueForReferralCode, setCookieWithExpiryForReferralCode } from '../../utils/cookies';
import { TextMessageConsent } from '../../constants/TextMessageConsent';
import useAlphaNumericInput from '../../Hooks/useAlphaNumericInput';
import { getUtmParameters } from '../../utils/utmTracker';
import CountryDropdown from '../../components/CountryDropdown/CountryDropdown';
import RegulatoryDisclosuresModal from '../../components/RegulatoryDisclosuresModal/RegulatoryDisclosuresModal';
import { normalizePhoneCode } from '../../utils/normalizePhoneCode';
import { generateBranchLinkAndRedirect, resetRedirectingFlag } from '../../utils/branchIoUtils';
import {handleLoginButtonNavigation} from '../../utils/handleLoginSignupRedirection'

const Signup = () => {
  const [firstName, setFirstName] = useState();
  const [lastName, setLastName] = useState();
  const [email, setEmail] = useState();
  const [phoneNumber, setPhoneNumber] = useState();
  const [password, setPassword] = useState();
  const [referredBy, setReferredBy] = useState(null);
  const [viewForm, setViewForm] = useState(true);
  const [loading, setLoading] = useState(false);
  const [disclosureOpen, setDisclosureOpen] = useState(false);
  const [isAgreementChecked, setIsAgreementChecked] = useState(false);
  const [subscribeForSMS, setSubscribeForSMS] = useState(false);
  const [isReferredByAutoFilled, setIsReferredByAutoFilled] = useState(false);
  const [countryCode, setCountryCode] = useState('US');
  const [countryPhoneCode, setCountryPhoneCode] = useState('1');
  const [accountType, setAccountType] = useState(ACCOUNT_TYPES.INDIVIDUAL);
  const [entityName, setEntityName] = useState();
  const [entityType, setEntityType] = useState();
  const [stateOfIncorporation, setStateOfIncorporation] = useState();
  const isEntity = accountType === ACCOUNT_TYPES.ENTITY;
  // Set once on mount: this visitor authenticated via Google/Apple (Login.js/SignupAuth.js) and
  // is completing their profile here instead of the old SocialNameUpdate modal. Read from
  // localStorage (not redux) so it survives a refresh — see src/utils/socialSignup.js.
  const [socialSignupState] = useState(() => getSocialSignupState());
  const isSocialSignup = socialSignupState !== null;
  const search = useLocation().search;
  const referralLink = new URLSearchParams(search).get('referredBy');
  const [emailLoading, setEmailLoading] = useState(false);
  const [form] = Form.useForm();
  const location = useLocation();
  const { onKeyDown, onInput, onPaste } = useAlphaNumericInput(15, (sanitizedText) => {
    setReferredBy(sanitizedText);
    form.setFieldValue('referredBy', sanitizedText);
  });
  let navigate = useNavigate();

  const isMobileDevice = () => {
    return isMobileDeviceDetectRegex;
  };

  const handleChangeNumber = (e) => {
    const { value } = e.target;
    const phoneNumber = formatPlainPhoneNumber(value);
    form.setFieldsValue({ phoneNumber: phoneNumber });
    setPhoneNumber(phoneNumber);
  };

  const handleCountryChange = (code, phone) => {
    setCountryCode(code);
    setCountryPhoneCode(`+${phone}`); // Add "+" prefix and ensure it's a string
  };

  const validateEmail = async () => {
    const token = process.env.REACT_APP_EMAIL_API_AUTH;
    const queryParams = new URLSearchParams(location.search);
    const getURLEmail = queryParams.get('email');

    if (getURLEmail) {
      try {
        setEmailLoading(true);
        const response = await EmailValidate(getURLEmail, token);
        if (response?.data?.data?.isEmailVerified) {
          navigate(`/login?email=${encodeURIComponent(getURLEmail)}`);
        } else {
          setEmail(getURLEmail);
          form.setFieldsValue({ email: getURLEmail });
        }
      } catch (error) {
        const { response } = error;
        toastNotify('error', response?.data?.message);
      } finally {
        setEmailLoading(false);
      }
    }
  };

  useEffect(() => {
    resetRedirectingFlag();
    if (isMobileDevice()) {
      const redirectIntendedPage = localStorage.getItem('redirectIntendedPage');
      if (!redirectIntendedPage) {
        generateBranchLinkAndRedirect(redirectIntendedPage)
      }
      // If redirectIntendedPage exists in localStorage, don't redirect
    }

    const utmParameters = getUtmParameters();
    const currentUrl = new URL(window.location.href);
    let hasUtmParams = false;

    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'gclid', 'fbclid', 'gbraid'];

    utmKeys.forEach(key => {
      if (utmParameters[key] && !currentUrl.searchParams.has(key)) {
        currentUrl.searchParams.set(key, utmParameters[key]);
        hasUtmParams = true;
      }
    });

    if (hasUtmParams) {
      navigate(currentUrl.pathname + currentUrl.search, { replace: true });
    }
  }, []);

  useEffect(() => {
    preventReload(form);
  }, []);

  useEffect(() => {
    // Social signup already carries a provider-verified email — skip the ?email= URL-param
    // verification path below and just lock it in directly.
    if (isSocialSignup) {
      setEmail(socialSignupState.email);
      form.setFieldsValue({ email: socialSignupState.email });
      return;
    }
    validateEmail();
  }, [location.search]);

  useEffect(() => {
    const referredByCode = getCookieValueForReferralCode('referralcode');
    if (referredByCode) {
      setReferredBy(referredByCode);
      form.setFieldsValue({ referredBy: referredByCode });
      setIsReferredByAutoFilled(true);
    }
  }, []);

  useEffect(() => {
    if (referralLink) {
      setCookieWithExpiryForReferralCode('referralcode', referralLink, 60);
      setReferredBy(referralLink);
      form.setFieldsValue({ referredBy: referralLink });
      setIsReferredByAutoFilled(true);
    }
  }, [referralLink]);

  const onFinish = async (values) => {
    let { phoneNumber, password, firstName, country, referredBy: formReferredBy, ...payload } = values;
    const phoneNumberInp = values.phoneNumber
      ? values.phoneNumber.replace(/\D/g, '') // Remove all non-numeric characters
      : '';
    payload.countryCode = countryCode;
    payload.countryPhoneCode = `+${normalizePhoneCode(countryPhoneCode)}`;
    payload.phoneNumber = phoneNumberInp;
    if (formReferredBy && formReferredBy.trim() !== '') {
      payload.referredBy = formReferredBy.trim().toUpperCase();
    }
    payload.subscribeForSMS = subscribeForSMS;
    payload.firstName = firstName?.trim() ?? '';
    if (isEntity) {
      payload.entityName = entityName?.trim();
      payload.entityType = entityType;
      payload.stateOfIncorporation = stateOfIncorporation?.trim();
    }

    setLoading(true);
    try {
      if (isSocialSignup) {
        // The user already authenticated via Google/Apple and never sets an app password —
        // this completes their profile through the same endpoint the old SocialNameUpdate
        // modal used (now also carrying accountType/entity fields), not account creation.
        payload.accountType = accountType.toUpperCase();
        const userData = await submitSocialProfile(payload);
        if (!userData.isError) {
          clearSocialSignupState();
          navigate('/sign-up/createpin');
        } else {
          toastNotify('error', userData?.message);
        }
        return;
      }

      payload.password = AES.encrypt(password, process.env.REACT_APP_PASSWORD_ENCRYPT_KEY).toString();

      // Get UTM parameters from localStorage
      const utmParameters = getUtmParameters();
      // accountType is sent as a query param (not in the payload) for manual (email/password)
      // signup, for both Individual and Entity.
      const queryParams = { accountType: accountType.toUpperCase() };
      // Add UTM parameters to query params if they exist
      if (utmParameters.utm_source) {
        queryParams.first_touch_utm_source = utmParameters.utm_source;
      }
      if (utmParameters.utm_medium) {
        queryParams.first_touch_utm_medium = utmParameters.utm_medium;
      }
      if (utmParameters.utm_campaign) {
        queryParams.first_touch_utm_campaign = utmParameters.utm_campaign;
      }
      if (utmParameters.utm_content) {
        queryParams.first_touch_utm_content = utmParameters.utm_content;
      }
      if (utmParameters.gclid) {
        queryParams.first_touch_gclid = utmParameters.gclid;
      }
      if (utmParameters.fbclid) {
        queryParams.first_touch_fbclid = utmParameters.fbclid;
      }
      if(utmParameters.gbraid) {
        queryParams.first_touch_gbraid = utmParameters.gbraid;
      }

      const userData = await signUp(payload, queryParams);
      if (!userData.isError) {
        localStorage.clear();
        const { accessToken, refreshToken = null } = userData.data;
        await localStorage.setItem('access_token_temp', accessToken);
        refreshToken && (await localStorage.setItem('refresh_token', refreshToken));
        await localStorage.setItem('referralLink', userData?.data?.user?.referralLink);
        localStorage.setItem('email', values.email);
        localStorage.setItem('phoneNo', values.phoneNumber);
        navigate('/sign-up/activation');
      } else {
        toastNotify('error', userData?.message);
      }
    } catch (error) {
      const { response } = error;
      toastNotify('error', response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='auth auth--details'>
      <AuthInfo
        className='hide'
        title={<>Real estate investing, one brick at a time</>}
        description={<>Start building your real estate portfolio, brick by brick.</>}
        note={
          <span className='underline cursor-pointer fw-600 auth__left__note' onClick={() => setDisclosureOpen(true)}>
            Company and Regulatory Disclosures
          </span>
        }
      >
        <div className='mobile-action' onClick={() => setViewForm(true)}>
          <Button className='w-100'>Get Started</Button>
        </div>
      </AuthInfo>
      <AuthForm
        className={viewForm ? 'show' : null}
        note={
          <>
            <span className='hide-mobile mr-4'>Already have an account?</span>
            <a href='/login' onClick={handleLoginButtonNavigation} className='underline fw-600'>
              Log in
            </a>
          </>
        }
        title={<>Let’s set up your profile.</>}
        description={<>This helps us personalize your experience and ensure the right account setup.</>}
      >
        <Form
          name='signup-form'
          className='auth-form details-form'
          onFinish={onFinish}
          // onFinishFailed={onFinishFailed}
          autoComplete='off'
          form={form}
        >
          <Spin spinning={emailLoading}>
            <div className='account-type'>
              <p className='account-type__label fw-600'>Account Type</p>
              <Row gutter={16}>
                <Col span={8}>
                  <div
                    className={`account-type__card ${accountType === ACCOUNT_TYPES.INDIVIDUAL ? 'account-type__card--active' : ''}`}
                    onClick={() => setAccountType(ACCOUNT_TYPES.INDIVIDUAL)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setAccountType(ACCOUNT_TYPES.INDIVIDUAL);
                      }
                    }}
                    role='button'
                    tabIndex={0}
                  >
                    <User />
                    <span>Individual</span>
                  </div>
                </Col>
                <Col span={8}>
                  <div
                    className={`account-type__card ${isEntity ? 'account-type__card--active' : ''}`}
                    onClick={() => setAccountType(ACCOUNT_TYPES.ENTITY)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setAccountType(ACCOUNT_TYPES.ENTITY);
                      }
                    }}
                    role='button'
                    tabIndex={0}
                  >
                    <EntityBuilding />
                    <span>Entity/Trust</span>
                  </div>
                </Col>
                <Col span={8}>
                  <div className='account-type__card account-type__card--disabled'>
                    <SelfDirectedIra />
                    <span>Self-Directed IRA – Coming Soon</span>
                  </div>
                </Col>
              </Row>
              <p className='account-type__subtext'>
                {isEntity ? 'Open an account for a trust, LLC, corporation, or other entity.' : 'Open an account in your personal name.'}
              </p>
            </div>
            <Row gutter={24}>
              <Col span={24} md={12}>
                <FloatingLabel
                  label={
                    <>
                      <b>Legal</b> First Name
                    </>
                  }
                  inputId='firstName'
                  inputValue={firstName}
                >
                  <Form.Item
                    validateTrigger='onBlur'
                    name='firstName'
                    rules={[
                      { required: true, message: FIRST_NAME_VALIDATION.REQUIRED },
                      {
                        transform: (value) => value?.trim(),
                        max: FIRST_NAME_LENGTHS.MAX,
                        message: FIRST_NAME_VALIDATION.MAX_LENGTH,
                      },
                      {
                        transform: (value) => value?.trim(),
                        min: FIRST_NAME_LENGTHS.MIN,
                        message: FIRST_NAME_VALIDATION.MIN_LENGTH,
                      },
                      {
                        pattern: alphabetSpaceRegex,
                        message: FIRST_NAME_VALIDATION.FORMAT,
                      },
                    ]}
                  >
                    <Input id='firstName' value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                  </Form.Item>
                </FloatingLabel>
              </Col>
              <Col span={24} md={12}>
                <FloatingLabel
                  label={
                    <>
                      <strong>Legal</strong> Last Name
                    </>
                  }
                  inputId='lastName'
                  inputValue={lastName}
                >
                  <Form.Item
                    validateTrigger='onBlur'
                    name='lastName'
                    rules={[
                      {
                        required: true,
                        message: LAST_NAME_VALIDATION.REQUIRED,
                      },
                      {
                        max: LAST_NAME_LENGTHS.MAX,
                        message: LAST_NAME_VALIDATION.MAX_LENGTH,
                      },
                      {
                        min: LAST_NAME_LENGTHS.MIN,
                        message: LAST_NAME_VALIDATION.MIN_LENGTH,
                      },
                      {
                        pattern: onlyAlphabetRegex,
                        message: LAST_NAME_VALIDATION.FORMAT,
                      },
                    ]}
                  >
                    <Input id='lastName' value={lastName} onChange={(e) => setLastName(e.target.value)} />
                  </Form.Item>
                </FloatingLabel>
              </Col>
            </Row>
            {isEntity && (
              <>
                <FloatingLabel label='Entity Name' inputId='entityName' inputValue={entityName}>
                  <Form.Item
                    validateTrigger='onBlur'
                    name='entityName'
                    rules={[
                      { required: true, message: ENTITY_NAME_VALIDATION.REQUIRED },
                      { max: ENTITY_NAME_LENGTHS.MAX, message: ENTITY_NAME_VALIDATION.MAX_LENGTH },
                      { pattern: /^\S/, message: ENTITY_NAME_VALIDATION.NO_LEADING_SPACE },
                    ]}
                  >
                    <Input id='entityName' value={entityName} onChange={(e) => setEntityName(e.target.value)} />
                  </Form.Item>
                </FloatingLabel>
                <SelectDropdown
                  label='Entity Type'
                  name='entityType'
                  inputId='entityType'
                  id='entityType'
                  initialValue={entityType}
                  options={ENTITY_TYPE_OPTIONS}
                  onChangeSelect={(_, value) => setEntityType(value)}
                  rules={[{ required: true, message: ENTITY_TYPE_VALIDATION.REQUIRED }]}
                />
                <FloatingLabel label='State of Incorporation' inputId='stateOfIncorporation' inputValue={stateOfIncorporation}>
                  <Form.Item
                    validateTrigger='onBlur'
                    name='stateOfIncorporation'
                    rules={[
                      { required: true, message: STATE_OF_INCORPORATION_VALIDATION.REQUIRED },
                      {
                        transform: (value) => value?.trim(),
                        max: STATE_OF_INCORPORATION_LENGTHS.MAX,
                        message: STATE_OF_INCORPORATION_VALIDATION.MAX_LENGTH,
                      },
                      {
                        transform: (value) => value?.trim(),
                        min: STATE_OF_INCORPORATION_LENGTHS.MIN,
                        message: STATE_OF_INCORPORATION_VALIDATION.MIN_LENGTH,
                      },
                      { pattern: alphabetSpaceRegex, message: STATE_OF_INCORPORATION_VALIDATION.FORMAT },
                    ]}
                  >
                    <Input
                      id='stateOfIncorporation'
                      value={stateOfIncorporation}
                      onChange={(e) => setStateOfIncorporation(e.target.value)}
                    />
                  </Form.Item>
                </FloatingLabel>
               
              </>
            )}
            <FloatingLabel label='Email' inputId='email' inputValue={email}>
              <EmailField email={email} setEmail={(value) => setEmail(value)} disabled={isSocialSignup} />
            </FloatingLabel>
            <Row gutter={[16, 0]}>
              <Col span={24} sm={8} md={8}>
                <CountryDropdown
                  country={countryCode}
                  setCountry={handleCountryChange}
                  isEdit={false}
                  required={true}
                />
              </Col>
              <Col span={24} sm={16} md={16}>
                <FloatingLabel label='Phone Number' inputId='phoneNumber' inputValue={phoneNumber}>
                  <Form.Item
                    name='phoneNumber'
                    validateTrigger='onBlur'
                    rules={[
                      {
                        required: true,
                        message: 'Please enter phone number',
                      },
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();
                          const digits = value.replace(/\D/g, '');
                          if (digits.length < 10) {
                            return Promise.reject(new Error('Phone number must be at least 10 digits.'));
                          }
                          if (digits.length > 10) {
                            return Promise.reject(new Error('Phone number must not exceed 10 digits.'));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      id='phoneNumber'
                      type='tel'
                      value={phoneNumber}
                      onChange={(e) => handleChangeNumber(e)}
                      onKeyPress={(event) => {
                        if (/\d/.test(event.key) && (phoneNumber || '').replace(/\D/g, '').length >= 10) {
                          event.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </FloatingLabel>
              </Col>
            </Row>
            {!isSocialSignup && (
              <FloatingLabel label='Password (min 8 characters)' shortLabel='Password' inputId='password' inputValue={password}>
                <PasswordField
                  autoCompletePassword={'new-password'}
                  name={'password'}
                  setPassword={(value) => setPassword(value)}
                  password={password}
                />
              </FloatingLabel>
            )}
            <FloatingLabel label='Referral Code' inputId='referredBy' inputValue={referredBy}>
              <Form.Item validateTrigger='onBlur' name='referredBy'>
                <Input
                  value={referredBy}
                  onChange={(e) => setReferredBy(e.target.value)}
                  disabled={isReferredByAutoFilled}
                  maxLength={15}
                  onKeyDown={onKeyDown}
                  onInput={onInput}
                  style={{ textTransform: 'uppercase' }}
                  onPaste={onPaste}
                />
              </Form.Item>
            </FloatingLabel>
            <div className='mb-10'>
              <Checkbox checked={isAgreementChecked} onChange={(e) => setIsAgreementChecked(e.target.checked)}>
                {' '}
                <>
                  I have read and agree to the Realbricks{' '}
                  <Link to={{ pathname: '/legal', hash: 'Terms of use' }} target='_blank' className='fw-600 underline'>
                    user account agreement
                  </Link>{' '}
                  and{' '}
                  <Link to={{ pathname: '/legal', hash: 'Privacy Policy' }} target='_blank' className='fw-600 underline'>
                    privacy policy
                  </Link>
                  .
                </>
              </Checkbox>
            </div>
            <div className='mb-40'>
              <Checkbox checked={subscribeForSMS} onChange={(e) => setSubscribeForSMS(e.target.checked)} disabled={!phoneNumber}>
                <TextMessageConsent />
              </Checkbox>
            </div>
            <Form.Item className='auth-form__submit details-form__submit' shouldUpdate>
              {() => (
                <Button
                  type='primary'
                  htmlType='submit'
                  className='w-100 ant-btn-primary--alt'
                  loading={loading}
                  disabled={
                    !firstName ||
                    !lastName ||
                    !email ||
                    (!isSocialSignup && !password) ||
                    !isAgreementChecked ||
                    (isEntity && (!entityName || !entityType || !stateOfIncorporation)) ||
                    form.getFieldsError().filter(({ errors }) => errors.length).length > 0 ||
                    !isAgreementChecked
                  }
                >
                  Sign Up
                </Button>
              )}
            </Form.Item>
          </Spin>
        </Form>
      </AuthForm>
      <RegulatoryDisclosuresModal open={disclosureOpen} onClose={() => setDisclosureOpen(false)} />
    </div>
  );
};

export default Signup;
